/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:52:59]
Permissions to update outside the main branch: [false]
*/

import {
    Button,
    Form,
    Modal,
    Pending,
    requiredRule,
    useGridFilterUrlContext,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { getProps } from 'axios/propApi';
import { BOProps } from 'base-components/BOProps/BOProps';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { DictionaryFilter as DictionaryFilterType, DictionaryTable } from 'models/api/dictionary';
import { PropExtended } from 'models/api/prop';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import 'scss/Main.scss';
import { useDictionaryWorkplaceState } from './Dictionary';

type DictionaryChangeModalProps = {
    isOpen: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
};

export default function DictionaryChangeModal({
    isOpen = false,
    toggle,
}: DictionaryChangeModalProps) {
    const { t } = useTranslation();
    const api = useApi();
    const { hasRole } = useAuth();
    const { cultures } = useGeneralSettingsContext();
    const { filter } = useGridFilterUrlContext<DictionaryFilterType>();
    const { currentItem, changeItemMutation, addItemMutation, tableData } =
        useDictionaryWorkplaceState();

    const [itemProps, setItemProps] = useState<PropExtended[]>([...(currentItem?.props || [])]);

    const { isFetching } = useQuery('DictItemProps', () => getProps(api, 'DictItem'), {
        onSuccess: (props) => {
            setItemProps(props);
        },
        enabled: isOpen && !itemProps.length,
    });

    const saveChanges = async (info: DictionaryTable) => {
        const props = (currentItem == null ? itemProps : info.props).concat();
        const translations = cultures
            .filter(
                (i) =>
                    (currentItem !== null &&
                        currentItem.translations[i.code] !== info.translations[i.code]) ||
                    (currentItem === null && info.translations[i.code])
            )
            .map((i) => ({
                cultureCode: i.code,
                value: info.translations[i.code],
            }));

        const dictCode = filter.dictCode || '';
        if (info.id) {
            changeItemMutation.mutate({
                id: info.id,
                translateCode: info.translateCode,
                name: info.name,
                dsc: info.dsc,
                notUsed: info.notUsed,
                sorting: info.sorting,
                translations: translations,
                props: props,
            });
        } else {
            addItemMutation.mutate({
                ...info,
                dictCode: dictCode,
                translateCode: dictCode + info.code,
                translations: translations,
                props: props,
            });
        }

        closeModal();
    };

    const closeModal = () => {
        toggle(false);
    };

    const formHasRole = (addRole: string, editRole: string): boolean =>
        currentItem?.id ? hasRole(editRole) : hasRole(addRole);

    const disabledInput = useMemo(
        () => !tableData.isUserEditable || !formHasRole('dictionaryCreate', 'dictionaryChange'),
        []
    );

    useEffect(
        () => setItemProps((prev) => prev.map((x) => ({ ...x, disabled: disabledInput }))),
        [disabledInput]
    );

    const { code, translateCode, dsc, id, name, notUsed, sorting, isUserEditable, translations } =
        currentItem || {};

    const defaultValues = {
        code,
        translateCode,
        dsc,
        id,
        name,
        notUsed,
        sorting,
        isUserEditable,
        translations,
        props: itemProps,
    };

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={closeModal}
            scrollable
            className="dictionary__modal"
            data-testid="dictionary-modal"
        >
            <Form<DictionaryTable>
                onSubmit={(data) => saveChanges(data)}
                defaultValues={defaultValues}
                className="form-in-modal"
            >
                <ModalHeader toggle={closeModal}>
                    {currentItem?.id ? `#${currentItem.id}` : t('BackOfficeNew')}
                </ModalHeader>
                <ModalBody className="dictionary__modal-body custom-scrollbar">
                    <ValidatedInput
                        type="checkbox"
                        name="notUsed"
                        disabled={disabledInput}
                        label={t('BackOfficeNotUsed')}
                    />
                    <hr />
                    <Row>
                        <Col>
                            <ValidatedInput<DictionaryTable>
                                name="code"
                                label={t('BackOfficeCode')}
                                disabled={
                                    !!currentItem?.id ||
                                    !formHasRole('dictionaryCreate', 'dictionaryChange')
                                }
                                rules={{ required: requiredRule }}
                            />
                        </Col>
                        <Col>
                            <ValidatedInput<DictionaryTable>
                                name="sorting"
                                type="number"
                                label={t('BackOfficeSorting')}
                                disabled={disabledInput}
                            />
                        </Col>
                    </Row>
                    <ValidatedInput<DictionaryTable>
                        name="name"
                        label={t('BackOfficeName')}
                        disabled={disabledInput}
                        rules={{ required: requiredRule }}
                    />
                    {isFetching ? <Pending /> : <BOProps<DictionaryTable> name="props" />}
                    {cultures &&
                        cultures.map((x) => {
                            return (
                                <ValidatedInput<DictionaryTable>
                                    key={`info-${x.code}-field`}
                                    name={`translations.${x.code}`}
                                    label={x.name}
                                    disabled={!formHasRole('dictionaryCreate', 'dictionaryChange')}
                                />
                            );
                        })}
                    <ValidatedInput<DictionaryTable>
                        id="info-dsc-field"
                        name="dsc"
                        label={t('BackOfficeDescription')}
                        disabled={disabledInput}
                        type="textarea"
                    />
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={closeModal}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button
                        color="success"
                        type="submit"
                        disabled={!formHasRole('dictionaryCreate', 'dictionaryChange')}
                    >
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}
